import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetItemsFluctuationData,
  useGetItemsFluctuationReport,
} from "src/api/reports";
import { useGetBranches } from "src/api/generic";
import DateRangeInput from "src/shared/components/date-range";
import ReportsIcon from "src/assets/svg-icons/side-bar/reports.svg";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import {
  downloadAsXLS,
  formatNumber,
  oneBranchHasPermission,
  specificBranchHasPermission,
} from "src/utils";

type FluctuationRow = {
  itemId: number;
  itemName: string;
  sku: string;
  fluctuationPercent: number;
};

type FluctuationItemApi = {
  item_id?: number;
  item_name: string;
  sku: string;
  fluctuation_percent: number;
};

function unwrapFluctuationItems(res: unknown): FluctuationItemApi[] {
  if (!res || typeof res !== "object") return [];
  const o = res as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === "object") {
    const items = (inner as Record<string, unknown>).items;
    if (Array.isArray(items)) return items as FluctuationItemApi[];
  }
  if (Array.isArray(o.items)) return o.items as FluctuationItemApi[];
  return [];
}

type TopItemsFluctuationFilters = {
  startDate: string;
  endDate: string;
  branchId: string;
};

const TopItemsFluctuationsPage = () => {
  const { t } = useTranslation("reports");
  const { t: purchasingT } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterText, setFilterText] = useState("");

  const formValues = useMemo((): TopItemsFluctuationFilters => {
    return {
      startDate:
        searchParams.get("start_date") ||
        moment().startOf("month").format("yyyy-MM-DD"),
      endDate:
        searchParams.get("end_date") ||
        moment().endOf("month").format("yyyy-MM-DD"),
      branchId: searchParams.get("branch_id") || "",
    };
  }, [searchParams]);

  const { control, setValue } = useForm<TopItemsFluctuationFilters>({
    values: formValues,
  });

  const { startDate, endDate, branchId: branchIdParam } = formValues;

  const { data: branchesData } = useGetBranches();

  const branchesHavePermission =
    branchesData?.data.filter((branch) =>
      specificBranchHasPermission("download_third_eye_report", branch.id)
    ) || [];

  const branchIdNum = branchIdParam ? Number(branchIdParam) : NaN;
  const branchValid = Number.isFinite(branchIdNum);

  const { data: reportResponse, isFetching } = useGetItemsFluctuationData({
    start_date: startDate,
    end_date: endDate,
    branches: branchValid ? [branchIdNum] : [],
    enabled: branchValid,
  });

  const { refetch: refetchFluctuationReport, isFetching: isDownloadingReport } =
    useGetItemsFluctuationReport({
      start_date: startDate,
      end_date: endDate,
      branches: branchValid ? [branchIdNum] : [],
      enabled: false,
    });

  const rows: FluctuationRow[] = useMemo(() => {
    const items = unwrapFluctuationItems(reportResponse);
    if (!items?.length) return [];
    return items.map((item) => ({
      itemId: item.item_id ?? 0,
      itemName: item.item_name,
      sku: item.sku,
      fluctuationPercent: item.fluctuation_percent,
    }));
  }, [reportResponse]);

  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.itemName.toLowerCase().includes(q) ||
        r.sku.toLowerCase().includes(q)
    );
  }, [rows, filterText]);

  const syncDatesToUrl = (start: string, end: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("start_date", start);
    next.set("end_date", end);
    setSearchParams(next);
  };

  const syncBranchToUrl = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("branch_id", id);
    else next.delete("branch_id");
    setSearchParams(next);
  };

  const goToItemDetailReport = (itemId: number) => {
    const p = new URLSearchParams();
    p.set("start_date", startDate);
    p.set("end_date", endDate);
    p.set("item_id", String(itemId));
    if (branchIdParam) {
      p.set("branch_id", branchIdParam);
    }
    navigate(`/reports/item-detail?${p.toString()}`);
  };

  const downloadReportsPermission = oneBranchHasPermission(
    "download_third_eye_report"
  );

  const handleDownload = () => {
    if (!branchValid) return;
    void refetchFluctuationReport().then((res) => {
      if (res.data != null) {
        downloadAsXLS(`\uFEFF${res.data}`, "items-fluctuation");
      }
    });
  };

  return (
    <Box gap={2} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      <Stack
        direction={"row"}
        spacing={"12px"}
        alignItems={"center"}
        color={"#101828"}
        mb={2}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"60px"}
          height={"60px"}
          bgcolor={"#D0D5DD"}
          borderRadius={"16px"}
        >
          <img src={ReportsIcon} width={"32px"} alt="" />
        </Box>
        <Typography fontSize={"32px"} lineHeight={"38.4px"} color={"#101828"}>
          {purchasingT("topItemsFluctuations")}
        </Typography>
      </Stack>

      <Stack spacing={2} direction={"row"} alignItems={"center"} flexWrap={"wrap"} mb={2}>
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
          <Typography color={"#344054"} fontWeight={600} lineHeight={"19.2px"}>
            {purchasingT("topItemsFluctuations")}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        flexWrap={"wrap"}
        gap={2}
        mb={2}
      >
        <Controller
          name="branchId"
          control={control}
          render={({ field }) => (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="top-items-branch" shrink>
                {generalT("branch")}
              </InputLabel>
              <Select
                {...field}
                labelId="top-items-branch"
                label={generalT("branch")}
                onChange={(e) => {
                  const v = e.target.value as string;
                  field.onChange(v);
                  syncBranchToUrl(v);
                }}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{generalT("selectBranch")}</em>
                </MenuItem>
                {branchesHavePermission.map((branch) => (
                  <MenuItem key={branch.id} value={String(branch.id)}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Box sx={{ minWidth: 250 }}>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <DateRangeInput
                startDate={
                  field.value ? moment(field.value) : null
                }
                startDateId="top-items-fluct-start"
                endDate={
                  formValues.endDate ? moment(formValues.endDate) : null
                }
                endDateId="top-items-fluct-end"
                onDatesChange={(arg: {
                  startDate: moment.Moment | null;
                  endDate: moment.Moment | null;
                }) => {
                  const s = arg.startDate?.format("yyyy-MM-DD");
                  const e = arg.endDate?.format("yyyy-MM-DD");
                  if (s && e) {
                    field.onChange(s);
                    setValue("endDate", e);
                    syncDatesToUrl(s, e);
                  }
                }}
              />
            )}
          />
        </Box>

        <TextField
          size="small"
          placeholder={t("search")}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{ minWidth: 220, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#98A2B3" }} fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <LoadingButton
          variant="outlined"
          color="tertiary"
          onClick={handleDownload}
          loading={isDownloadingReport}
          disabled={
            !branchValid || !downloadReportsPermission
          }
          sx={{ height: 40, minWidth: 120 }}
        >
          {generalT("download")}
        </LoadingButton>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #EAECF0",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box bgcolor={"#F9FAFB"} px={2} py={1.5}>
          <Typography fontWeight={600} color={"#101828"}>
            {t("itemsReport")}
          </Typography>
        </Box>
        {!branchValid ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">
              {t("selectBranchToViewReport")}
            </Typography>
          </Box>
        ) : isFetching ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }}>
                    {t("itemName")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }}>
                    {t("sku")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }} align="right">
                    {t("fluctuationPercent")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }} align="center" width={72}>
                    {generalT("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary" align="center" py={2}>
                        {generalT("noData")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row, index) => {
                    const pct = row.fluctuationPercent;
                    const color =
                      pct >= 0 ? "#12B76A" : "#F04438";
                    return (
                      <TableRow key={`${row.itemId}-${row.sku}-${index}`}>
                        <TableCell>{row.itemName}</TableCell>
                        <TableCell>{row.sku}</TableCell>
                        <TableCell align="right" sx={{ color, fontWeight: 500 }}>
                          {formatNumber(pct)}%
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t("viewItemDetail")}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={t("viewItemDetail")}
                                disabled={!row.itemId}
                                onClick={() => goToItemDetailReport(row.itemId)}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default TopItemsFluctuationsPage;
